import { gql } from "@apollo/client";

// ============================================================================
// EXPERTISE QUERIES
// ============================================================================

import { GENERAL_FIELDS, SITE_FIELDS, PAGE_SETTINGS } from "./shared";
import {
  SECTIONS_WRAPPER_QUERY,
  EXPERTISE_FIELDS,
  EXPERTISE_OVERVIEW_META,
} from "./fragments/sections";
import { RELATED_CONTENT_SECTION_QUERY } from "./fragments/related-content";

// ============================================================================
// EXPERTISE FRAGMENTS
// ============================================================================

// EXPERTISE_FIELDS is now imported from ./fragments/sections

export const EXPERTISES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${RELATED_CONTENT_SECTION_QUERY}
  query GetExpertises($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: expertisesOverview(locale: $locale) {
      # Expertises Overview Meta (SEO)
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Expertises Overview Fields (Content)
      title
      shortTitle
      intro {
        value
      }
      callToActions {
        __typename
        ... on ButtonRecord {
          ...ButtonBlock
        }
        ... on ButtonExternalRecord {
          ...ButtonExternalBlock
        }
        ... on DownloadButtonRecord {
          ...DownloadButtonBlock
        }
        ... on HubspotButtonRecord {
          ...HubspotButtonBlock
        }
      }
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      # Expertises Overview Components
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
    }
    expertises: allExpertises(
      locale: $locale
      first: 100
      orderBy: position_ASC
      filter: { _locales: { allIn: [$locale] } }
    ) {
      ...ExpertiseFields
    }
  }
`;

export const EXPERTISE_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${EXPERTISE_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${RELATED_CONTENT_SECTION_QUERY}
  ${EXPERTISE_OVERVIEW_META}
  query GetExpertise($slug: String!, $locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    overview: expertisesOverview {
      ...ExpertiseOverviewMeta
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: expertise(filter: { slug: { eq: $slug } }, locale: $locale) {
      # Expertise Meta (SEO)
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Expertise Fields (Content)
      ...ExpertiseFields

      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      callToActions {
        __typename
        ... on ButtonRecord {
          ...ButtonBlock
        }
        ... on ButtonExternalRecord {
          ...ButtonExternalBlock
        }
        ... on DownloadButtonRecord {
          ...DownloadButtonBlock
        }
        ... on HubspotButtonRecord {
          ...HubspotButtonBlock
        }
      }
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
    }
  }
`;

export const ALL_EXPERTISE_SLUGS_QUERY = gql`
  query GetAllExpertiseSlugs($locale: SiteLocale!) {
    allExpertises(locale: $locale, first: 100) {
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }
    }
  }
`;
